import {
  Box,
  Button,
  Icon,
  Theme,
  Typography,
  styled,
} from "@outschool/backpack";
import * as FilestackUrls from "@outschool/filestack-urls";
import { ParentProfileQuery } from "@outschool/gql-frontend-generated";
import { faChevronDoubleDown } from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import {
  addParamsToUrl,
  enrolledPath,
  parentPath,
  parentPrivatePath,
} from "@outschool/routes";
import * as Time from "@outschool/time";
import { useLoginFlowContext, useSession } from "@outschool/ui-auth";
import { ActivityListingPhoto } from "@outschool/ui-components-classroom";
import { OutreachCard } from "@outschool/ui-components-website";
import { Container } from "@outschool/ui-legacy-component-library";
import { Screen } from "@outschool/ui-utils";
import _ from "lodash";
import React, { useState } from "react";
import { useHistory, useLocation } from "react-router";

import * as Routes from "../../../shared/Routes";
import { useTimeZone } from "../../providers/TimeZoneProvider";
import FooterWithLocalization from "../Footer";
import HeaderTags from "../HeaderTags";
import SimpleLayout from "../layouts/SimpleLayout";
import Link from "../Link";
import { MainPageBelowTheFold } from "../MainPageBelowTheFold";
// @ts-ignore - JSX import
import MessageModal from "../MessageModal";
import PhotoReviewCarousel from "../reviews/PhotoReviewCarousel";
import { PublicReviewsByParentModal } from "../reviews/PublicReviewsByParentModal";
import WrittenReviewCarousel from "../reviews/WrittenReviewCarousel";
import { MyFavoritesCarousel } from "./MyFavorites";
import ParentClassHistoryPaginated from "./MyRecentClasses";
import { MyTeachersSection } from "./MyTeachersSection";
import { ParentProfileCard } from "./ParentProfileCard";
import { ProfileLinkSelect } from "./ProfileLinkSelect";
import { ReferralSignupCard } from "./ReferralSignupCard";
import { getReferrerSlugId } from "./utils";

const StyledCard = styled(Box)({
  padding: "16px 20px",
  borderRadius: "16px",
  boxShadow: "0 16px 32px 0 #0000001A, 0 0 4px 0 #0000000A",
});

export function ParentProfilePage({
  parent,
  isCurrentUsersProfile,
  profileLinkUid,
  publicView = false,
}: {
  parent: NonNullable<
    NonNullable<ParentProfileQuery["publicProfile"]>["parent"]
  >;
  isCurrentUsersProfile: boolean;
  publicView?: boolean;
  profileLinkUid?: string;
}) {
  const isMobile = Screen.useIsMobile();
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const { setAllowSignUpModal } = useLoginFlowContext();
  const history = useHistory();

  React.useEffect(() => {
    setAllowSignUpModal(false);
    return setAllowSignUpModal(true);
  });

  const { timeZone: userTimeZone } = useTimeZone();
  const { isAdmin, isLoggedIn, currentUser } = useSession();
  const location = useLocation();
  const { t } = useTranslation("client\\routes\\ParentProfile");

  const isEducator =
    !!currentUser?.teacher_approved_at && !currentUser?.suspended_at;

  const publicLinkNoPreview = parentPath(currentUser?.uid || "");
  const publicLink = addParamsToUrl(publicLinkNoPreview, {
    preview: true,
  });

  const privateLinkNoPreview = parentPrivatePath({
    uid: currentUser?.uid || "",
    profile_link_uid: currentUser?.profile_link_uid || "",
  });
  const privateLink = addParamsToUrl(privateLinkNoPreview, {
    preview: true,
  });

  React.useEffect(() => {
    // rewrite the url so users can copy directly from url to share
    isCurrentUsersProfile &&
      !publicView &&
      history.replace(
        addParamsToUrl(
          currentUser?.privacy_state === "anyone"
            ? publicLinkNoPreview
            : privateLinkNoPreview,
          location.query.preview ? { preview: true } : {},
        ),
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.privacy_state]);

  const privacyState = currentUser?.privacy_state;

  const { sectionsWithCurrentUser = [] } = parent;

  const title = parent.publicName ?? "An Outschool parent";

  const description = parent.introduction
    ? _.truncate(parent.introduction, { length: 280, separator: /,? +/ })
    : t(
        "{{parentName}} is on Outschool to discover our small-group online classes for kids. Join us!",
        { parentName: parent.publicName },
      );

  const referrerSlugId = getReferrerSlugId();

  return (
    <>
      <SimpleLayout
        hideFooter
        narrowColumn
        contentSx={{ maxWidth: 1280, marginBottom: "2em" }}
      >
        <HeaderTags
          title={title}
          meta={[
            {
              property: "og:type",
              content: "profile",
            },
            { name: "description", content: description },
            {
              property: "og:title",
              content: title,
            },
            {
              property: "og:description",
              content: description,
            },
            { property: "og:url", content: Routes.parentUrl(parent.uid) },
            {
              property: "og:image",
              content: FilestackUrls.imageUrl(parent.photo, {
                w: 300,
                h: 300,
                fit: "crop",
              }),
            },
            { property: "og:image:width", content: "300" },
            { property: "og:image:height", content: "300" },
            { name: "twitter:card", content: "summary_large_image" },
            { name: "robots", content: "noindex" },
          ]}
          link={[{ rel: "canonical", href: Routes.parentUrl(parent.uid) }]}
        />
        {isMobile && !isLoggedIn && referrerSlugId && (
          <Box sx={{ p: 24 }}>
            <ReferralSignupCard referrerSlugId={referrerSlugId} />
            <Box
              as="a"
              aria-label={t("Skip to profile")}
              href="#main-content"
              sx={{ display: "block", textAlign: "center", p: 8 }}
            >
              <Icon icon={faChevronDoubleDown} size="medium" color="primary" />
            </Box>
          </Box>
        )}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr",
            gridGap: 40,
            p: 20,
            "@media (min-width: 1075px)": {
              gridTemplateColumns: "300px 1fr",
              p: 0,
              py: 24,
              paddingRight: "1rem",
              paddingLeft: "2rem",
              alignItems: "start",
            },
          }}
          id="main-content"
        >
          <Box
            sx={{
              "@media (min-width: 1075px)": {
                position: "sticky",
                top: 15,
              },
            }}
          >
            <StyledCard sx={{ mb: 16 }}>
              <ParentProfileCard
                parent={parent}
                isCurrentUsersProfile={!publicView}
                privateLink={
                  currentUser?.privacy_state === "anyone"
                    ? publicLink
                    : privateLink
                }
              />
            </StyledCard>
            {isEducator && (
              <OutreachCard
                width="100%"
                outreachShareUrl={Routes.parentProfileWithLinkUidJoinUrl(
                  isCurrentUsersProfile ? currentUser : parent,
                  { utm_campaign: "share_parent_link" },
                )}
              />
            )}
            <Box sx={{ margin: 15, mt: "5rem" }}>
              <ProfileLinkSelect
                isCurrentUsersProfile={!publicView}
                publicLink={publicLink}
                publicLinkNoPreview={publicLinkNoPreview}
                privateLinkNoPreview={privateLinkNoPreview}
                privacyState={privacyState}
              />
            </Box>
            <Box
              sx={(theme: Theme) => ({
                backgroundColor: "#FFF",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "space-between",
                textAlign: "center",
                paddingX: "1em",
                maxWidth: "100%",
                [theme.breakpoints.up("md")]: {
                  paddingX: "2em",
                },
              })}
            >
              <ParentActions
                parent={parent}
                isAdmin={isAdmin}
                location={location}
                sectionsWithCurrentUser={sectionsWithCurrentUser}
                currentUser={currentUser}
                isLoggedIn={isLoggedIn}
              />
            </Box>

            {!isMobile && !isLoggedIn && referrerSlugId && (
              <StyledCard>
                <ReferralSignupCard referrerSlugId={referrerSlugId} />
              </StyledCard>
            )}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <MyTeachersSection
              parent={parent}
              profileLinkUid={profileLinkUid}
              privacyState={privacyState}
              privateLink={privateLink}
              isCurrentUsersProfile={!publicView}
              publicView={publicView}
            />

            <ParentClassHistoryPaginated
              privacyState={privacyState}
              privateLink={privateLink}
              parent={parent}
              isCurrentUsersProfile={!publicView}
              profileLinkUid={profileLinkUid}
              publicView={publicView}
            />
            <MyFavoritesCarousel
              parentUid={parent.uid}
              isCurrentUsersProfile={!publicView}
              profileLinkUid={profileLinkUid}
              privacyState={privacyState}
              privateLink={privateLink}
              publicView={publicView}
            />
            <PhotoReviewCarousel
              parent={parent}
              isCurrentUsersProfile={!publicView}
              title={
                <Typography emphasized variant={"h6"}>
                  {t("Photos from My Reviews")}
                </Typography>
              }
            />
            <WrittenReviewCarousel
              parent={parent}
              isReviewModalOpen={isReviewModalOpen}
              setIsReviewModalOpen={setIsReviewModalOpen}
              isCurrentUsersProfile={!publicView}
              title={
                <Typography emphasized variant={"h6"}>
                  {t("My Recent Reviews")}
                </Typography>
              }
            />
            <PublicReviewsByParentModal
              parentUid={parent.uid}
              isReviewModalOpen={isReviewModalOpen}
              setIsReviewModalOpen={setIsReviewModalOpen}
            />
            <SectionsWithCurrentUser
              parent={parent}
              sectionsWithCurrentUser={sectionsWithCurrentUser}
              userTimeZone={userTimeZone}
            />
          </Box>
        </Box>
      </SimpleLayout>
      {publicView && (
        <>
          <MainPageBelowTheFold />
          <FooterWithLocalization />
        </>
      )}
    </>
  );
}

function SectionsWithCurrentUser({
  sectionsWithCurrentUser = [],
  userTimeZone,
  parent,
}: {
  sectionsWithCurrentUser: NonNullable<
    NonNullable<ParentProfileQuery["publicProfile"]>["parent"]
  >["sectionsWithCurrentUser"];
  userTimeZone: string;
  parent: NonNullable<
    NonNullable<ParentProfileQuery["publicProfile"]>["parent"]
  >;
}) {
  const { t } = useTranslation("client\\routes\\ParentProfile");
  if (sectionsWithCurrentUser.length === 0) {
    return null;
  }
  return (
    <Container
      size="small"
      sx={{ paddingY: "medium", backgroundColor: "white" }}
    >
      <Typography
        variant="h3"
        sx={{
          marginBottom: "1em",
          paddingTop: "0.5em",
        }}
      >
        {t("You and {{parentPublicName}} both enrolled in:", {
          parentPublicName: parent.publicName,
        })}
      </Typography>
      <Box
        sx={{
          marginBottom: "2em",
        }}
      >
        {sectionsWithCurrentUser.map(section => {
          return (
            <CommonSection
              // @ts-ignore TS(2339): Property 'uid' does not exist on type 'never'.
              key={section.uid}
              // @ts-ignore TS(2339): Property 'leader' does not exist on type 'never'.
              leader={section.leader}
              section={section}
              // @ts-ignore TS(2339): Property 'activity' does not exist on type 'never'... Remove this comment to see the full error message
              activity={section.activity}
              userTimeZone={userTimeZone}
            />
          );
        })}
      </Box>
    </Container>
  );
}

function CommonSection({ leader, activity, section, userTimeZone }: $TSFixMe) {
  return (
    <Typography variant="inherit">
      <Box
        id={activity.uid}
        sx={(theme: Theme) => ({
          display: "block",
          padding: "0.75em !important",

          [theme.breakpoints.up("md")]: {
            display: "flex",
          },
        })}
      >
        <Box
          sx={(theme: Theme) => ({
            width: "100%",
            textAlign: "left",

            [theme.breakpoints.up("md")]: {
              width: "22%",
              flexShrink: 0,
            },
          })}
        >
          <Link to={enrolledPath(section.uid)}>
            <ActivityListingPhoto activity={activity} />
          </Link>
        </Box>
        <Box
          sx={(theme: Theme) => ({
            width: "100%",
            textAlign: "left",

            [theme.breakpoints.up("md")]: {
              width: "74%",
              marginLeft: "4%",
              flexShrink: 0,
            },
          })}
        >
          <Box>
            <Link to={enrolledPath(section.uid)}>{activity.title}</Link>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            {leader && leader.name}
          </Box>
          <Box>
            {_.truncate(activity.summary, { length: 280, separator: /,? +/ })}
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              color: "#808080",
            }}
          >
            <Box>
              {Time.pastDurationString(
                section.start_time,
                section.end_time,
                userTimeZone,
              )}
              &nbsp;
            </Box>
          </Box>
        </Box>
      </Box>
    </Typography>
  );
}

function ParentActions({
  parent,
  location,
  sectionsWithCurrentUser,
  currentUser,
  isLoggedIn,
  isAdmin,
}: $TSFixMe) {
  const { t } = useTranslation("client\\routes\\ParentProfile");
  const canMessage =
    sectionsWithCurrentUser && sectionsWithCurrentUser.length > 0;
  return (
    <Box
      sx={{
        marginLeft: "auto",
        marginRight: "auto",
        maxWidth: "400px",
      }}
    >
      {canMessage && (
        <MessageModal
          renderButton={(open: $TSFixMe) => (
            <Button
              onClick={open}
              sx={{
                width: "100%",
                boxSizing: "border-box",
              }}
            >
              {t("Send message")}
            </Button>
          )}
          user={currentUser}
          location={location}
          requireLogin={true}
          isLoggedIn={isLoggedIn}
          viewName="sendMessageToParent"
          heading={`Send ${parent.name} a message`}
          recipients={[parent]}
          context={{
            action: "Message to Parent",
            parentUid: parent.uid,
            parentName: parent.name,
            subject: "Question for " + parent.name,
          }}
        />
      )}
      {isAdmin && (
        /* eslint-disable jsx-a11y/accessible-emoji */
        <Box
          sx={{
            marginBottom: "1em",
            marginTop: "1em",
          }}
        >
          <Link
            to={Routes.userAdminPath(parent)}
            sx={(theme: Theme) => ({
              width: "100%",
              [theme.breakpoints.up("md")]: {
                width: "auto",
              },
            })}
          >
            {/* eslint-disable-next-line i18next/no-literal-string */}
            <Typography variant="inherit" role="img" aria-label="top hat">
              🎩
            </Typography>
            {t(" Admin")}
          </Link>
        </Box>
      )}
    </Box>
  );
}
